import {DrawerNavigationCategoryTree_TreeItem} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation/v1/drawer_navigation.types';
import {useTranslation} from '@aboutyou/i18n';
import {useTrackOriginClick} from '@aboutyou/tracking';
import {
  CategoryTile,
  GenericIconName,
  Icon,
  UnstyledButton,
  UnstyledLink,
} from '@aboutyou/ui-components';
import React, {FC, useRef} from 'react';
import styled from 'styled-components';
import {useIsSelectedDiscountAppOnly} from '../../Campaigns/SelectedDiscounts/useSelectedDiscount';
import {useSelectedDiscountColor} from '../../Campaigns/SelectedDiscounts/useSelectedDiscountColor';
import {useSelectedDiscountUrl} from '../../Campaigns/SelectedDiscounts/useSelectedDiscountUrl';
import {useFocusOnFirstButtonElement} from '../categoryTreeFocusManagementHelpers';
import {useNavigationCategory} from '../useNavigationCategory';
import {AllDealsTile} from './shared/AllDealsTile';
import {CategoryItemWrapper} from './shared/CategoryItemWrapper';
import {CategoryTreeNavigationItem} from './shared/CategoryTreeNavigationItem';
import {Label, StyledMenu, UnstyledList} from './shared/StyledComponents';
import {normalizeThumbnail} from './shared/normalizeThumbnail';

export interface NextLevelCategoryTreeProps {
  category: DrawerNavigationCategoryTree_TreeItem;
  categoryParent?: number;
  isTransitioning: boolean;
  testId: string;
  onCategoryClick?: VoidFunction;
}

export const NextLevelCategoryTree: React.FC<NextLevelCategoryTreeProps> = ({
  category,
  categoryParent,
  isTransitioning,
  testId,
  onCategoryClick,
}) => {
  const scrollAnchorRef = useRef<HTMLDivElement>(null);
  const {t} = useTranslation();
  const trackOrigin = useTrackOriginClick();
  const {setCurrentCategoryId} = useNavigationCategory();
  const discountUrl = useSelectedDiscountUrl();
  const discountColor = useSelectedDiscountColor();
  const isAppOnly = useIsSelectedDiscountAppOnly();
  const listRef = useFocusOnFirstButtonElement();

  const trackCategoryClick = trackOrigin('category_list');
  const saleCount =
    (category.categoryPreview?.$case === 'productsCount' &&
      category.categoryPreview.productsCount.saleCount) ||
    0;

  return (
    <nav>
      <NextLevelHeader
        targetCategory={categoryParent}
        title={category.category.name}
        onCategoryClick={onCategoryClick}
      />
      <StyledMenu data-testid={testId} data-is-transitioning={isTransitioning}>
        <UnstyledList data-increase-grid-gap={true} ref={listRef}>
          {saleCount > 0 && (
            <li onClick={onCategoryClick} tabIndex={-1}>
              <UnstyledLink
                data-testid="categoryTreeNavigationItem_AllDeals"
                to={`${category.category.path}?hasCampaignReduction=true`}
                aria-label={undefined}
              >
                <CategoryTile
                  backgroundColor={category.saleIcon?.color.hex}
                  labelSize="px12"
                  labelColor="Text.White"
                  label={
                    <>
                      {t('All deals in')}&nbsp;{category.category.name}
                    </>
                  }
                />
              </UnstyledLink>
            </li>
          )}

          {discountUrl && !isAppOnly && (
            <AllDealsTile
              color={discountColor!}
              linkTarget={`${discountUrl.url}&hasCampaignReduction=true`}
            />
          )}

          <CategoryItemWrapper
            key={category.category.id}
            item={category}
            handleClick={() => {
              trackCategoryClick();
            }}
          >
            <CategoryTile
              label={t('ShowAll')}
              labelSize="px12"
              thumbnail={normalizeThumbnail({
                categoryPreview: category.categoryPreview,
                isShowAll: true,
              })}
            />
          </CategoryItemWrapper>

          {category.children.map(
            item =>
              item.category && (
                <CategoryItemWrapper
                  key={item.category.id}
                  item={item}
                  handleClick={e => {
                    trackCategoryClick();
                    if (
                      !item.category.isLeafCategory &&
                      !!item.children.length
                    ) {
                      e.preventDefault();
                      setCurrentCategoryId(item.category.id);
                      scrollAnchorRef.current?.scrollIntoView?.({
                        behavior: 'auto',
                        block: 'start',
                      });
                    }
                  }}
                >
                  <CategoryTreeNavigationItem
                    item={item}
                    shouldReduceTextSize
                  />
                </CategoryItemWrapper>
              ),
          )}
        </UnstyledList>
      </StyledMenu>
    </nav>
  );
};

const NextLevelHeader: FC<{
  targetCategory?: number;
  title: string;
  onCategoryClick?: VoidFunction;
}> = ({targetCategory, title, onCategoryClick}) => {
  const {setCurrentCategoryId} = useNavigationCategory();
  const onClickBackButton = () => {
    setCurrentCategoryId(targetCategory);
    onCategoryClick?.();
  };

  return (
    <NextLevelHeaderContainer>
      <UnstyledButton
        data-testid="categoryNavigationBack"
        onClick={onClickBackButton}
        aria-label={undefined}
      >
        <Icon size="S" asset={GenericIconName.ArrowLeft} />
      </UnstyledButton>
      <Label weight="bold" size="px20" textAlignment="center">
        {title}
      </Label>
    </NextLevelHeaderContainer>
  );
};

const NextLevelHeaderContainer = styled.div`
  display: flex;
  padding: 18px 0;
  margin-right: 24px;
  justify-content: space-between;
`;
